import { httpsCallable } from '../firebase/app'
import { isNativePlatform } from './runtime'
import { getOrCreateDeviceId } from './adapters/deviceIdentity'

/**
 * Client-side service for #1696's three LOCATION_CHALLENGE contracts
 * (spec.md §10.2): `getLocationChallenge`, `submitLocationSample`,
 * `submitLocationUnavailable`. Same "every function here is a no-op on web"
 * shape as `nativeDeviceRegistry.js` (#1691) — there is no background
 * location challenge concept on web/PWA at all (§2.2), so every export
 * resolves `null` there instead of letting the callable round-trip fail.
 *
 * Every callable in this file's own server-side contract (see
 * `functions/nativeApp/getLocationChallenge.js`,
 * `functions/nativeApp/submitLocationSample.js`,
 * `functions/nativeApp/submitLocationUnavailable.js`) requires `deviceId` to
 * confirm the CALLING device is the one a given challenge was addressed to
 * — every export here resolves it via `getOrCreateDeviceId()` rather than
 * asking the caller to pass it, so `adapters/location.js` only ever deals
 * in `challengeId`.
 */

/**
 * Fetches a challenge's own metadata — deliberately NOT the vehicle
 * location (see `getLocationChallenge.js`'s header for why).
 *
 * @param {string} challengeId
 * @return {Promise<?{challengeId:string, eventType:string, expiresAt:*, expired?:boolean, status:string}>} null on web.
 *   `expired` is the server's verdict (#2170); absent from an older server.
 */
export async function fetchLocationChallenge(challengeId) {
  if (!isNativePlatform()) {
    return null
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('getLocationChallenge')({ challengeId, deviceId })
  return result?.data ?? null
}

/**
 * Submits one raw GPS sample for the server to validate against the
 * vehicle location it already holds (§8.4) — this call never receives or
 * decides MATCH/MISMATCH itself.
 *
 * @param {object} params Parameters.
 * @param {string} params.challengeId
 * @param {string} params.sampledAt ISO timestamp of the fix.
 * @param {number} params.lat
 * @param {number} params.lng
 * @param {number} params.accuracy Reported horizontal accuracy, meters.
 * @param {string} params.idempotencyKey
 * @return {Promise<?{result:string, reason:?string, policyVersion:?number}>} null on web.
 */
export async function submitLocationChallengeSample({ challengeId, sampledAt, lat, lng, accuracy, idempotencyKey }) {
  if (!isNativePlatform()) {
    return null
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('submitLocationSample')({
    challengeId, deviceId, sampledAt, lat, lng, accuracy, idempotencyKey,
  })
  return result?.data ?? null
}

/**
 * Reports that no sample could be taken at all, with a machine-readable
 * reason (§8.3 item 6's alt path) — the device never decides
 * LOCATION_UNAVAILABLE's consequence (REQUIRED blocks / ADVISORY warns),
 * only why no sample exists.
 *
 * @param {object} params Parameters.
 * @param {string} params.challengeId
 * @param {string} params.reason One of the LOCATION_UNAVAILABLE_REASONS values
 *   (`functions/nativeApp/locationChallengeModel.js` — the wire contract's
 *   values, mirrored client-side in `adapters/location.js`, see that file's
 *   own comment on why there is no shared import path here).
 * @return {Promise<?{result:string, reason:string, policyVersion:?number}>} null on web.
 */
export async function reportLocationChallengeUnavailable({ challengeId, reason }) {
  if (!isNativePlatform()) {
    return null
  }
  const deviceId = await getOrCreateDeviceId()
  const result = await httpsCallable('submitLocationUnavailable')({ challengeId, deviceId, reason })
  return result?.data ?? null
}
